/* eslint-disable prettier/prettier */
import React, { useState, useEffect } from 'react';
import styles from '../../../styles/components/pageComponents/program/ProgramContentBuilder.module.css';
import { ModuleItem } from '../program/content/ModuleItem';
import { ModuleType, ActivityType } from '../../../types/content';
import { CourseModule } from '../../../services/courseModule.service';

// Helper to generate quick unique IDs
const generateId = () => Math.random().toString(36).substr(2, 9);

type ModuleUI = ModuleType & {
    backendId?: string;
    isDeleted?: boolean;
    isDirty?: boolean;
    activities: ActivityUI[];
    startdate: string | Date | null;
    enddate: string | Date | null;

    errors?: {
        name?: string;
        startdate?: string;
        enddate?: string;
    };
};

type ActivityUI = ActivityType & {
    backendId?: string;
    isDeleted?: boolean;
    isDirty?: boolean;

    typeTitle?: string;
    typeColor?: string;

    errors?: {
        name?: string;
        type?: string;
        date?: string;
        startTime?: string;
        endTime?: string;
    };
};

type Props = {
    courseId: string;
    modules: CourseModule[];
    fetchCourseModules: any;
    fetchModuleLessons: any;
    createModule: any;
    updateModule: any;
    deleteModule: any;
    createLesson: any;
    updateLesson: any;
    deleteLesson: any;
    onClose?: () => void;
    onSave?: (modules: ModuleType[]) => void;
    onError?: (error: any) => void;
};

export const ProgramContentBuilder: React.FC<Props> = ({
    courseId,
    modules: apiModules,
    fetchCourseModules,
    fetchModuleLessons,
    createModule,
    updateModule,
    deleteModule,
    createLesson,
    updateLesson,
    deleteLesson,
    onClose,
    onSave,
    onError,
}) => {

    const [activitiesWithTimeErrors, setActivitiesWithTimeErrors] =
        useState<Set<string>>(new Set());

    const [modules, setModules] = useState<ModuleUI[]>([
        {
            id: generateId(),
            name: '',
            startdate: null,
            enddate: null,
            description: '',
            // activities: [
            //     {
            //         id: generateId(),
            //         name: '',
            //         type: '',
            //         date: '',
            //         startTime: '',
            //         endTime: '',
            //         description: '',
            //         backendId: undefined,
            //         isDeleted: false,
            //     },
            // ],
            activities: [],
        },
    ]);

    const combineDateAndTime = (dateStr: string, timeStr: string) => {
        if (!dateStr || !timeStr) return null;
        const [hours, minutes] = timeStr.split(':').map(Number);
        const date = new Date(dateStr);
        date.setHours(hours, minutes);
        return date.toISOString();
    };

    const calculateDuration = (
        startDateTime: string | null,
        endDateTime: string | null
    ): number => {
        if (!startDateTime || !endDateTime) return 0;

        const start = new Date(startDateTime);
        const end = new Date(endDateTime);

        const diffInMinutes =
            (end.getTime() - start.getTime()) / (1000 * 60);

        return Math.max(0, Math.round(diffInMinutes));
    };

    // ── Removed: showSuccess state ──
    // The success notification is now handled by the parent (AdminEditProgramPage)
    // via the onSave callback, which calls its own showSuccess() + refreshDetails().

    const totalActivities = modules.reduce(
        (acc, curr) => acc + curr.activities.length,
        0
    );

    const totalDurationMinutes = modules.reduce((moduleAcc, module) => {
        return (
            moduleAcc +
            module.activities.reduce((activityAcc, activity) => {
                if (activity.isDeleted) return activityAcc;

                const start = combineDateAndTime(
                    activity.date,
                    activity.startTime
                );

                const end = combineDateAndTime(
                    activity.date,
                    activity.endTime
                );

                return (
                    activityAcc +
                    calculateDuration(start, end)
                );
            }, 0)
        );
    }, 0);

    const formattedDuration = React.useMemo(() => {
        const hours = Math.floor(totalDurationMinutes / 60);
        const minutes = totalDurationMinutes % 60;

        return `${hours}h ${minutes}m`;
    }, [totalDurationMinutes]);

    const extractDate = (isoString: string) => {
        if (!isoString) return '';
        const date = new Date(isoString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: '2-digit',
        });
    };

    const extractTime = (isoString: string) => {
        if (!isoString) return '';
        const date = new Date(isoString);
        return date.toTimeString().slice(0, 5); // HH:mm
    };

    useEffect(() => {
        console.log('🔥 RAW apiModules:', apiModules);

        if (!apiModules?.length) return;

        const mapped = apiModules.map((m: any) => {
            console.log('🧩 Mapping module:', m);
            return {
                id: generateId(),
                backendId: m.id,
                name: m.title || '',
                startdate: m.startdate ? new Date(m.startdate) : null,
                enddate: m.enddate ? new Date(m.enddate) : null,
                description: m.description || '',
                activities: (m.lessons || []).map((l: any) => ({
                    id: generateId(),
                    backendId: l.id,
                    name: l.title || '',
                    description: l.description || '',
                    type: l.activitytypeid || '',
                    startdate: l.scheduledstarttime
                        ? new Date(l.scheduledstarttime)
                        : null,
                    enddate: l.scheduledendtime
                        ? new Date(l.scheduledendtime)
                        : null,
                    date: l.scheduledstarttime
                        ? new Date(l.scheduledstarttime)
                            .toISOString()
                            .split('T')[0]
                        : '',
                    startTime: extractTime(l.scheduledstarttime),
                    endTime: extractTime(l.scheduledendtime),
                })),
            };
        });

        console.log('✅ MAPPED modules:', mapped);

        setModules(mapped);
    }, [apiModules]);



    // ─────────────────────────────────────────────
    // Module Handlers
    // ─────────────────────────────────────────────

    const handleAddModule = () => {
        setModules([
            ...modules,
            {
                id: generateId(),
                name: '',
                startdate: null,
                enddate: null,
                description: '',
                activities: [],
            },
        ]);
    };

    const handleUpdateModule = (id: string, field: any, value: any) => {
        console.log('✏️ Updating module:', { id, field, value });
        setModules(prev =>
            prev.map(mod =>
                mod.id === id
                    ? {
                        ...mod,
                        [field]: value,
                        isDirty: true,
                        errors: {
                            ...mod.errors,
                            [field]: undefined, // ✅ clear error
                        },
                    }
                    : mod
            )
        );
    };

    const handleDeleteModule = (id: string) => {
        setModules(prev =>
            prev.map(mod => (mod.id === id ? { ...mod, isDeleted: true } : mod))
        );
    };

    const handleDeleteActivity = (moduleId: string, activityId: string) => {
        setModules(prev =>
            prev.map(mod => {
                if (mod.id === moduleId) {
                    return {
                        ...mod,
                        activities: mod.activities.map(act =>
                            act.id === activityId
                                ? { ...act, isDeleted: true }
                                : act
                        ),
                    };
                }
                return mod;
            })
        );
    };
    // --- Activity Handlers ---
    const handleAddActivity = (moduleId: string) => {

        const module = modules.find(m => m.id === moduleId);

        // Module must exist in DB first
        if (!module?.backendId) {
            alert('Please save the module first before adding activities.');
            return;
        }
        setModules(
            modules.map(mod => {
                if (mod.id === moduleId) {
                    return {
                        ...mod,
                        activities: [
                            ...mod.activities,
                            {
                                id: generateId(),
                                name: '',
                                type: '',
                                date: '',
                                startTime: '',
                                endTime: '',
                                description: '',
                                isDirty: true,
                            },
                        ],
                    };
                }
                return mod;
            })
        );
    };

    const handleUpdateActivity = (
        moduleId: string,
        activityId: string,
        field: keyof ActivityType,
        value: any,
        errors?: ActivityUI['errors'],
        title?: string,
        color?: string
    ) => {
        console.log('✏️ Updating activity:', {
            moduleId,
            activityId,
            field,
            value,
        });
        setModules(
            modules.map(mod => {
                if (mod.id === moduleId) {
                    return {
                        ...mod,
                        activities: mod.activities.map(act =>
                            act.id === activityId
                                ? {
                                    ...act,
                                    [field]: value,

                                    errors: errors ?? act.errors,

                                    ...(field === 'type' && {
                                        typeTitle: title,
                                        typeColor: color,
                                    }),
                                    isDirty: true,
                                }
                                : act
                        ),
                    };
                }
                return mod;
            })
        );
    };

    const validateModules = () => {
        let isValid = true;

        const updatedModules = modules.map(module => {
            if (module.isDeleted) return module;

            const moduleErrors: any = {};

            if (!module.name?.trim()) {
                moduleErrors.name = 'Module name is required';
                isValid = false;
            }

            if (
                !module.startdate ||
                isNaN(new Date(module.startdate).getTime())
            ) {
                moduleErrors.startdate = 'Start date is required';
                isValid = false;
            }

            if (!module.enddate) {
                moduleErrors.enddate = 'End date is required';
                isValid = false;
            }

            const updatedActivities = module.activities.map(activity => {
                if (activity.isDeleted) return activity;

                const isEmpty =
                    !activity.name &&
                    !activity.type &&
                    !activity.date &&
                    !activity.startTime &&
                    !activity.endTime;

                if (isEmpty) return activity;

                const activityErrors: any = {};

                if (!activity.name?.trim()) {
                    activityErrors.name = 'Activity name is required';
                    isValid = false;
                }

                if (!activity.type) {
                    activityErrors.type = 'Activity type is required';
                    isValid = false;
                }

                if (activity.date && !activity.startTime) {
                    activityErrors.startTime =
                        'Start time is required when a date is selected';
                    isValid = false;
                }

                if (activity.date && !activity.endTime) {
                    activityErrors.endTime =
                        'End time is required when a date is selected';
                    isValid = false;
                }
                // if (!activity.date) {
                //     activityErrors.date = 'Date is required';
                //     isValid = false;
                // }

                // if (!activity.startTime) {
                //     activityErrors.startTime = 'Start time required';
                //     isValid = false;
                // }

                // if (!activity.endTime) {
                //     activityErrors.endTime = 'End time required';
                //     isValid = false;
                // }

                return {
                    ...activity,
                    errors: activityErrors,
                };
            });

            return {
                ...module,
                errors: moduleErrors,
                activities: updatedActivities,
            };
        });

        setModules(updatedModules);
        return isValid;
    };

    // ─────────────────────────────────────────────
    // Save Handler
    // ─────────────────────────────────────────────



    const isFormValid = React.useMemo(() => {
        const activeModules = modules.filter(m => !m.isDeleted);

        // If no module exists, disable save
        if (activeModules.length === 0) {
    // If there are modules marked for deletion,
    // keep Save enabled so deletions can be synced.
    const hasDeletedModules = modules.some(
        m => m.isDeleted && m.backendId
    );

    return hasDeletedModules;
}

        return activeModules.every(module => {

            if (module.errors && Object.values(module.errors).some(Boolean)) {
                return false;
            }
            // Required module fields
            const moduleValid =
                module.name?.trim() &&
                module.startdate &&
                module.enddate;

            if (!moduleValid) return false;

            // Validate only activities that have been started
            return module.activities.every(activity => {

                if (activity.errors && Object.values(activity.errors).some(Boolean)) {
                    return false;
                }

                if (activity.isDeleted) return true;

                const isEmpty =
                    !activity.name &&
                    !activity.type &&
                    !activity.date &&
                    !activity.startTime &&
                    !activity.endTime;

                // Empty activity row is allowed
                if (isEmpty) return true;

                return (
                    activity.name?.trim() &&
                    activity.type
                );
            });
        });
    }, [modules]);

    const formatDateForApi = (date: string | Date) => {
        const d = new Date(date);

        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
    };

    const handleSaveAll = async () => {
        for (let mIndex = 0; mIndex < modules.length; mIndex++) {
            const module = modules[mIndex];

            // DELETE MODULE
            if (module.isDeleted && module.backendId) {
                await deleteModule(module.backendId);
                continue;
            }
            if (module.isDeleted) continue;

            let moduleId = module.backendId;

            console.log('💾 Module before save:', module);

            const basePayload = {
                title: module.name,
                description: module.description,
                orderindex: mIndex,
                startdate: module.startdate
                    ? formatDateForApi(module.startdate)
                    : null,

                enddate: module.enddate
                    ? formatDateForApi(module.enddate)
                    : null,
                moduletype: 'theory',
                duration: 0,
            };

            console.log('📦 Payload:', basePayload);
            // CREATE MODULE
            if (!module.backendId) {
                const res = await createModule({
                    ...basePayload,
                    courseid: courseId,
                });

                moduleId = res?.id;

                if (!moduleId) {
                    console.error(
                        '❌ Module creation failed, skipping lessons'
                    );
                    continue;
                }

                setModules(prev =>
                    prev.map(m =>
                        m.id === module.id
                            ? { ...m, backendId: moduleId, isDirty: false }
                            : m
                    )
                );
            } else if (module.isDirty) {
                // UPDATE MODULE
                await updateModule(module.backendId, basePayload);
            }

            // PROCESS LESSONS
            for (let lIndex = 0; lIndex < module.activities.length; lIndex++) {
                const lesson = module.activities[lIndex];

                if (lesson.isDeleted && lesson.backendId) {
                    await deleteLesson(lesson.backendId);
                    continue;
                }
                if (lesson.isDeleted) continue;

                if (!moduleId) {
                    console.error('❌ No moduleId, skipping lesson');
                    continue;
                }


                const scheduledStartTime = combineDateAndTime(
                    lesson.date,
                    lesson.startTime
                );

                const scheduledEndTime = combineDateAndTime(
                    lesson.date,
                    lesson.endTime
                );

                const duration = calculateDuration(
                    scheduledStartTime,
                    scheduledEndTime
                );
                const lessonPayload = {
                    title: lesson.name,
                    description: lesson.description,
                    orderindex: lIndex,
                    scheduledstarttime: scheduledStartTime,
                    scheduledendtime: scheduledEndTime,
                    duration,
                    isrequired: true,
                    activitytypeid: lesson.type,
                    islive: true,
                };

                if (!lesson.backendId) {
                    // CREATE
                    const res = await createLesson({
                        ...lessonPayload,
                        moduleid: moduleId,
                    });

                    setModules(prev =>
                        prev.map(m =>
                            m.id === module.id
                                ? {
                                    ...m,
                                    activities: m.activities.map(a =>
                                        a.id === lesson.id
                                            ? {
                                                ...a,
                                                backendId: res?.id,
                                                isDirty: false,
                                            }
                                            : a
                                    ),
                                }
                                : m
                        )
                    );
                } else if (lesson.isDirty) {
                    // UPDATE
                    try {
                        await updateLesson(lesson.backendId, lessonPayload);
                    } catch (error) {

                    }
                }
            }
        }

        console.log('✅ All modules & lessons synced');
    };

    // ─────────────────────────────────────────────
    // Render
    // ─────────────────────────────────────────────

    return (
        <div className={styles.container}>
            <header className={styles.pageHeader}>
                <h1 className={styles.title}>
                    Update Program Details - Program Content
                </h1>
                <div className={styles.headerRight}>
                    <div className={styles.actions}>
                        <button
                            className={`${styles.btn} ${styles.btnCancel}`}
                            onClick={onClose}
                        >
                            Cancel
                        </button>

                        <button
                            className={`${styles.btn} ${styles.btnSave}`}
                            disabled={!isFormValid}
                            onClick={async () => {
                                const isValid = validateModules();

                                if (!isValid) {
                                    console.log('❌ Validation failed');
                                    return;
                                }

                                try {
                                    await handleSaveAll();
                                    onSave?.(modules);
                                    onClose?.();
                                } catch (err) {
                                    console.error('❌ Save failed:', err);
                                    onError?.(err);
                                }
                            }}
                        >
                            Save Changes
                        </button>
                    </div>

                    <div className={styles.stats}>
                        {modules.filter(m => !m.isDeleted).length} Modules{' '}
                        <span className={styles.dot}>•</span>
                        {totalActivities} Activities{' '}
                        <span className={styles.dot}>•</span>
                        {formattedDuration} Total Duration
                    </div>
                </div>
            </header>

            <div className={styles.scrollArea}>
                <div className={styles.modulesList}>
                    {modules
                        .filter(mod => !mod.isDeleted)
                        .map((moduleData, index) => {
                            console.log('MODULE DATA', moduleData);

                            return (
                                <ModuleItem
                                    key={moduleData.id}
                                    moduleData={moduleData}
                                    index={index}
                                    canAddActivity={!!moduleData.backendId}
                                    onUpdate={handleUpdateModule}
                                    onDelete={handleDeleteModule}
                                    onAddActivity={handleAddActivity}
                                    onUpdateActivity={handleUpdateActivity}
                                    onDeleteActivity={handleDeleteActivity}
                                    setActivitiesWithTimeErrors={
                                        setActivitiesWithTimeErrors
                                    }
                                />
                            );
                        })}
                </div>

                <div className={styles.addModuleWrapper}>
                    <button
                        className={`${styles.btn} ${styles.btnAddText}`}
                        onClick={handleAddModule}
                    >
                        Add Module <span>+</span>
                    </button>
                </div>

                {/* ── Removed: inline SuccessContent block ──
                    Reason: it rendered at the bottom of the scroll area,
                    invisible to the user, and was unmounted by onClose()
                    before anyone could see it. The parent now owns this UI. */}
            </div>
        </div>
    );
};
